import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Config } from '@services';
import { UnitService } from '../../pages/units/units.service';
import { SupportDialogComponent } from '../support-dialog';
import { AwareSecurityIsGodDirective, AwareSecurityService } from '@appbolaget/aware-security';
import { arrayFilterAsync } from '@appbolaget/helpers';
import { debounceTime, delay, filter, map, share, startWith, switchMap, take, tap } from 'rxjs/operators';
import { SidenavItem } from './symbols';
import { Unit } from '@appbolaget/aware-model';
import { Observable, combineLatest, firstValueFrom } from 'rxjs';
import { AwareExtensionService, AwareExtensionState } from '@appbolaget/aware-extension';
import { Store } from '@ngxs/store';
import { UnitState } from 'app/state/unit.state';
import { AwareAuthService } from '@appbolaget/aware-auth';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IsActiveRoutePipe } from '@helpers/pipes';
import { MediaService } from '@modules/media/services/media.service';

@Component({
    selector: 'app-sidenav',
    templateUrl: 'sidenav.component.html',
    styleUrls: ['./sidenav.scss'],
    providers: [UnitService, IsActiveRoutePipe],
    host: {
        '[class.is-mobile]': 'isMobile()',
        '[class.is-collapsed]': 'isCollapsed()',
    },
    imports: [
        MatExpansionModule,
        MatIconModule,
        MatTooltipModule,
        AwareSecurityIsGodDirective,
        IsActiveRoutePipe,
        TranslateModule,
        RouterModule,
        CommonModule,
    ],
})
export class SidenavComponent implements OnInit {
    config = inject(Config);
    router = inject(Router);
    matDialog = inject(MatDialog);
    unitService = inject(UnitService);
    private awareSecurityService = inject(AwareSecurityService);
    private awareExtensionService = inject(AwareExtensionService);

    items$: Observable<SidenavItem[]>;
    org: Unit;
    unit: Unit;

    opened = true;
    currentOpenItems: Set<SidenavItem> = new Set();

    // Flyout menu for collapsed mode
    activeFlyout = signal<{ item: SidenavItem; top: number } | null>(null);

    // Responsive state passed from parent
    readonly isMobile = input<boolean>(false);
    readonly isCollapsed = input<boolean>(false);

    // Event emitted when a nav item is clicked (used to close sidenav on mobile)
    readonly navItemClicked = output<void>();

    #store = inject(Store);
    #awareAuth = inject(AwareAuthService);
    #isActiveRoute = inject(IsActiveRoutePipe);
    #mediaService = inject(MediaService);

    org$: Observable<Unit> = this.#store.select(UnitState.hierarchy).pipe(
        switchMap((units) =>
            this.#awareAuth.clientFiltered$.pipe(
                map((client) => client.units),
                map((clientUnits) => {
                    const hierarchy = [...units];
                    const reversed = [...hierarchy].reverse();
                    return reversed.find((unit) => clientUnits.some((clientUnit) => clientUnit.uuid === unit.uuid));
                }),
            ),
        ),
    );

    async ngOnInit() {
        this._initListeners();
    }

    getSidenavConfig(): Promise<SidenavItem[]> {
        const self = this;

        return new Promise((resolve, _) => {
            const configNavigation = this.config.get<SidenavItem[]>('unitConfig.panel.sidenav.items');

            if (!configNavigation) {
                resolve(self.config.get<SidenavItem[]>('sidenav.items'));
            } else {
                resolve(configNavigation);
            }
        });
    }

    navigationItemWithMethodClicked(item: SidenavItem) {
        if (this[item.method]) {
            this[item.method]();
        }
    }

    openSupportModal() {
        this.matDialog.open(SupportDialogComponent);
    }

    openMediaDialog() {
        this.#mediaService.openDialog();
    }

    showFlyout(event: MouseEvent, item: SidenavItem) {
        if (!this.isCollapsed() || !item.children || item.children.length === 0) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();

        const target = event.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();

        this.activeFlyout.set({
            item,
            top: rect.top,
        });
    }

    closeFlyout() {
        this.activeFlyout.set(null);
    }

    onNavItemClick() {
        if (this.isMobile()) {
            this.navItemClicked.emit();
        }
    }

    private async _filterItems(items: SidenavItem[]) {
        const disabledFeatures: string[] = this.config.get('unitConfig.shared.disabledFeatures') ?? [];
        const myUnits = this.#awareAuth.client?.units ?? [];

        const filterItem = (item: SidenavItem) => {
            if (item.unitType) {
                if (Array.isArray(item.unitType) && !item.unitType.includes(this.unit.type)) {
                    return false;
                } else if (!Array.isArray(item.unitType) && item.unitType !== this.unit.type) {
                    return false;
                }
            }

            if (item.required_unit_connection_type) {
                if (Array.isArray(item.required_unit_connection_type)) {
                    const hasConnectionType = item.required_unit_connection_type.some((type) => myUnits.some((unit) => unit.type === type));
                    if (!hasConnectionType) {
                        return false;
                    }
                } else if (!myUnits.some((unit) => unit.type === item.required_unit_connection_type)) {
                    return false;
                }
            }

            if (disabledFeatures.includes(`route:${item.path}`)) {
                return false;
            }

            if (Array.isArray(item.unitType)) {
                return item.unitType.includes(this.unit.type);
            }

            if (item.children) {
                item.children = item.children.filter(filterItem);

                if (item.children.length === 0) {
                    return false;
                }

                if (item.children.length === 1) {
                    item.path = item.children[0].path;
                    item.queryParams = item.children[0].queryParams;
                    item.required_permissions = item.children[0].required_permissions;
                    item.children = null;
                }
            }

            return true;
        };

        items = items.filter(filterItem);

        return arrayFilterAsync(items, async (item: SidenavItem) => {
            const isGranted = await firstValueFrom(this.awareSecurityService.isGranted(item.required_permissions).pipe(take(1)));
            if (!isGranted) {
                return false;
            }

            if (item.required_extension) {
                const hasExtension = await firstValueFrom(
                    this.awareExtensionService.hasExtensionWithType(item.required_extension).pipe(take(1)),
                );

                if (!hasExtension) {
                    return false;
                }
            }

            if (item.children) {
                item.children = await this._filterItems(item.children);

                if (item.children.length === 0) {
                    return false;
                }

                if (item.children.length === 1) {
                    item.title = item.children[0].title;
                    item.path = item.children[0].path;
                    item.queryParams = item.children[0].queryParams;
                    item.required_permissions = item.children[0].required_permissions;
                    item.children = null;
                }
            }

            return true;
        });
    }

    private _initListeners() {
        const unit$ = this.#store.select(UnitState.activeUnit).pipe(
            filter((unit) => unit instanceof Unit),
            delay(10), // To avoid ExpressionChangedAfterItHasBeenCheckedError
            tap((unit) => {
                this.unit = unit;
                this.org = this.unitService.getHighestParentFromUnit(unit);
            }),
        );

        const extensions$ = this.#store.select(AwareExtensionState.extensions);

        const token$ = this.#awareAuth.token$.pipe(filter((token) => !!token));

        this.items$ = combineLatest([unit$, token$, extensions$]).pipe(
            debounceTime(300),
            switchMap(([unit]) =>
                this.getSidenavConfig().then((config) => {
                    const contactsItem: SidenavItem = {
                        title: 'Kontakter',
                        icon: 'contacts',
                        path: `units/${unit.uuid}/contacts`,
                        required_permissions: 'unit.read',
                    };

                    const items = [...config];
                    const clientsIndex = items.findIndex((item) => item.path === 'clients');
                    if (clientsIndex !== -1) {
                        items.splice(clientsIndex + 1, 0, contactsItem);
                    } else {
                        items.push(contactsItem);
                    }

                    return items;
                }),
            ),
            switchMap((config) => this._filterItems(config)),
            share(),
        );

        const routerNavigation$ = this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            startWith(null),
        );

        combineLatest([routerNavigation$, this.items$])
            .pipe(tap(([_, items]) => this.#determineCurrentOpenItems(items, this.router.url)))
            .subscribe();
    }

    #determineCurrentOpenItems(items: SidenavItem[], currentUrl: string): void {
        const itemReducer = (acc: SidenavItem[], item: SidenavItem) => {
            if (item.children) {
                const children = item.children.reduce(itemReducer, []);
                if (children.length > 0) {
                    acc.push(item, ...children);
                }
            } else if (item.path) {
                if (this.#isActiveRoute.transform(item.path, item.queryParams, currentUrl)) {
                    acc.push(item);
                }
            }
            return acc;
        };

        const currentOpenItems = items.reduce(itemReducer, []);
        this.currentOpenItems = new Set(currentOpenItems);
    }
}
